import clsx from 'clsx';
import React, { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { FormError } from '../FormElements/FormElements';
import { Icon } from '../Icon/Icon';

import styles from './FileUploadDropzone.module.scss';

interface FileAcceptType {
  [key: string]: string[];
}

interface FileExcludeType {
  extensions?: string[];
  mimeTypes?: string[];
}

export type UploadedFile = {
  name: string;
  file: File;
};

export interface NewFileUploadProps {
  value?: UploadedFile | UploadedFile[] | File | File[] | null;
  onChange: (value: UploadedFile | UploadedFile[] | null) => void;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;

  buttonLabel?: string;
  placeholder?: string;
  fileTypeDescription?: string;

  acceptTypes?: FileAcceptType;
  excludeTypes?: FileExcludeType;

  error?: string;

  showFileList?: boolean;
  maxFileListItems?: number;
}

export interface FileDropzoneProps {
  value?: UploadedFile | UploadedFile[] | File | File[] | null;
  onChange: (value: UploadedFile | UploadedFile[] | null) => void;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;

  buttonLabel?: string;
  fileTypeDescription?: string;

  acceptTypes?: FileAcceptType;
  excludeTypes?: FileExcludeType;

  error?: string;

  showFileList?: boolean;
  maxFileListItems?: number;
}

const normalizeToUploadedFiles = (value?: FileDropzoneProps['value']): UploadedFile[] => {
  if (!value) return [];
  const items = Array.isArray(value) ? value : [value];
  return items
    .map(item => {
      if (!item) return null;
      if (item instanceof File) return { name: item.name, file: item } as UploadedFile;
      return item as UploadedFile;
    })
    .filter(Boolean) as UploadedFile[];
};

const downloadLocalFile = (file: File) => {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const isExcluded = (fileItem: File, excludeTypes?: FileExcludeType) => {
  if (!excludeTypes) return false;
  const name = fileItem.name.toLowerCase();
  const type = fileItem.type.toLowerCase();
  return (
    excludeTypes.extensions?.some(ext => name.endsWith(ext.toLowerCase())) ||
    excludeTypes.mimeTypes?.some(m => type.includes(m.toLowerCase())) ||
    false
  );
};

const getAcceptPattern = (acceptTypes?: FileAcceptType, excludeTypes?: FileExcludeType) => {
  if (acceptTypes) return acceptTypes;
  if (!excludeTypes) return undefined;

  const commonTypes: Record<string, string[]> = {
    'text/*': [],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'text/plain': ['.txt'],
    'text/csv': ['.csv'],
    'text/html': ['.html'],
    'text/markdown': ['.md'],
    'application/json': ['.json'],
    'application/xml': ['.xml'],
    'application/zip': ['.zip'],
    'application/x-rar-compressed': ['.rar'],
    'application/x-7z-compressed': ['.7z'],
    'image/*': [],
    'video/*': [],
    'audio/*': [],
  };

  const filtered = { ...commonTypes };
  if (excludeTypes.extensions) {
    Object.keys(filtered).forEach(mime => {
      const exts = filtered[mime];
      if (exts.length > 0) {
        filtered[mime] = exts.filter(
          ext => !excludeTypes.extensions?.some(ex => ext.toLowerCase() === ex.toLowerCase()),
        );
        if (filtered[mime].length === 0) delete filtered[mime];
      }
    });
  }
  return filtered;
};

export const FileUploadDropzone: React.FC<FileDropzoneProps> = ({
  value,
  onChange,
  multiple = false,
  disabled = false,
  className,
  buttonLabel,
  fileTypeDescription,
  acceptTypes,
  excludeTypes,
  error,
  showFileList = false,
  maxFileListItems = 6,
}) => {
  const [typeError, setTypeError] = useState<string | null>(null);
  const selectedFiles = useMemo<UploadedFile[]>(() => normalizeToUploadedFiles(value), [value]);

  const onDrop = useCallback(
    (newFiles: File[]) => {
      if (!newFiles) {
        setTypeError('Please upload the file of the correct type');
        return;
      }
      const nonEmpty = newFiles.filter(f => f.size > 0);
      if (nonEmpty.length === 0) {
        setTypeError('Selected file(s) are empty. Please select files in the correct format.');
        return;
      }
      const valid = excludeTypes ? nonEmpty.filter(f => !isExcluded(f, excludeTypes)) : nonEmpty;
      if (valid.length === 0) {
        setTypeError('Selected file(s) are not allowed. Please select files of the correct type.');
        return;
      }
      const uploaded: UploadedFile[] = valid.map(f => ({ name: f.name, file: f }));
      if (multiple) {
        onChange([...normalizeToUploadedFiles(value), ...uploaded]);
      } else {
        onChange(uploaded[0] ?? null);
      }
      setTypeError(null);
    },
    [excludeTypes, multiple, onChange, value],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    disabled,
    noClick: true,
    accept: getAcceptPattern(acceptTypes, excludeTypes),
    multiple,
  });

  const handleRemove = () => onChange(multiple ? [] : null);

  const handleRemoveAtIndex = (index: number) => {
    if (!multiple) return;
    onChange(selectedFiles.filter((_, i) => i !== index));
  };

  const hasFiles = selectedFiles.length > 0;
  const showFileChip = !multiple && hasFiles;

  return (
    <div className={className}>
      <div
        {...getRootProps()}
        className={clsx(styles.dropzone, {
          [styles.isDragActive]: isDragActive,
          [styles.hasFile]: showFileChip,
          [styles.isError]: !!typeError || !!error,
          [styles.isDisabled]: disabled,
        })}
      >
        <input
          className={styles.input}
          {...getInputProps()}
        />

        {showFileChip ? (
          <div className={styles.fileChip}>
            <Icon
              className={styles.fileIcon}
              name="file"
            />
            <span className={styles.chipName}>{selectedFiles[0].name}</span>
            <button
              type="button"
              className={styles.removeBtn}
              onClick={e => {
                e.stopPropagation();
                handleRemove();
              }}
              disabled={disabled}
            >
              <Icon name="close" />
            </button>
          </div>
        ) : (
          <>
            <Icon
              className={styles.uploadIcon}
              name="upload_import"
            />
            <div className={styles.idleText}>
              {multiple && hasFiles ? (
                <>
                  {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected ·{' '}
                  <button
                    type="button"
                    className={styles.chooseLink}
                    onClick={e => {
                      e.stopPropagation();
                      open();
                    }}
                    disabled={disabled}
                  >
                    {buttonLabel || 'add more'}
                  </button>
                </>
              ) : (
                <>
                  Drag &amp; drop, or{' '}
                  <button
                    type="button"
                    className={styles.chooseLink}
                    onClick={e => {
                      e.stopPropagation();
                      open();
                    }}
                    disabled={disabled}
                  >
                    {buttonLabel || (multiple ? 'choose files' : 'choose file')}
                  </button>
                </>
              )}
            </div>
            {fileTypeDescription && <div className={styles.dzHint}>{fileTypeDescription}</div>}
          </>
        )}
      </div>

      {showFileList && multiple && hasFiles && (
        <ul className={styles.fileList}>
          {selectedFiles.slice(0, maxFileListItems).map((file, index) => (
            <li
              key={`${file.name}-${file.file.size}-${file.file.lastModified}`}
              className={styles.fileListItem}
            >
              <button
                type="button"
                className={styles.fileLink}
                onClick={() => downloadLocalFile(file.file)}
                disabled={disabled}
              >
                {file.name}
              </button>
              <button
                type="button"
                className={styles.removeItem}
                onClick={() => handleRemoveAtIndex(index)}
                disabled={disabled}
                aria-label={`Remove ${file.name}`}
              >
                ×
              </button>
            </li>
          ))}
          {selectedFiles.length > maxFileListItems && <li>…and {selectedFiles.length - maxFileListItems} more</li>}
        </ul>
      )}

      {!typeError && error && (
        <FormError
          className={styles.errorLabel}
          error={error}
        />
      )}
      {typeError && (
        <FormError
          className={styles.errorLabel}
          error={typeError}
        />
      )}
    </div>
  );
};
